import type { HomeBenefits } from '@aithora/shared';
import type { MemberCtx } from './MemberHome';
import { facts, numbered } from './Sheet';
import { Act, D, DASH, S } from './ui';
import { useCustomerText } from '../../ui/buildLabels';

// Benefits (design T11487-11510, hTabMoney). Every item, its amount and its sheet wait
// on the PROPOSED GET /athome/me/:id/benefits; the start button is a member action.

const BTN = (primary: boolean) =>
  'display:inline-flex;align-items:center;height:44px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;' +
  (primary ? 'background:#2F5C97;color:#fff;' : 'color:#3C454F;border:1px solid rgba(34,39,46,.18);') +
  'margin-top:12px;height:48px;font-size:16px;border-radius:11px;';
const SHEET_COLOR: Record<HomeBenefits['items'][number]['kind'], string> = { otc: '#12503C', waiver: '#2F5C97', modification: '#4A5866', medicare: '#54606C', other: '#2F5C97' };
const usd = (n: number) => `$${n.toLocaleString('en-US')}`;

export function BenefitsTab({ c }: { c: MemberCtx }) {
  const plain = useCustomerText();
  const b = c.d.benefits.data;
  const items = b?.items ?? [];
  const claim = items.filter((m) => m.claimable && m.amountUsd);
  const total = claim.reduce((n, m) => n + (m.amountUsd ?? 0), 0);
  return (
    <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(330px,1fr));gap:10px;min-width:0;align-items:start;">
      <D css="grid-column:1/-1;box-sizing:border-box;border-radius:18px;padding:20px 22px;min-width:0;background:rgba(47,138,111,.10);border:1px solid rgba(47,138,111,.3);display:flex;align-items:center;gap:20px;flex-wrap:wrap;row-gap:12px;animation:riseIn .46s cubic-bezier(.22,.61,.36,1);">
        <S css="flex:1 1 240px;min-width:0;">
          <S css="display:block;font-size:16px;font-weight:700;letter-spacing:.1em;color:#12503C;">YOURS TO CLAIM</S>
          <S css="display:block;font-size:44px;font-weight:700;letter-spacing:-.035em;line-height:1.02;color:#12503C;margin-top:4px;">{!b ? DASH : claim.length ? `${usd(total)} unclaimed` : 'nothing unclaimed'}</S>
          <S css="display:block;font-size:17px;color:#3C454F;line-height:1.5;margin-top:8px;text-wrap:pretty;">
            {b
              ? 'The thing that ends aging at home is usually money, not health. Anything you are owed and have not claimed shows here.'
              : c.d.benefits.error
                ? plain('Nothing to show yet. Allowances, waivers and funding wait on the benefits feed (GET /athome/me/:id/benefits), and we will not guess an amount.')
                : ''}
          </S>
        </S>
        {/* Nothing to claim, nothing to start: the button shows only with something to claim. */}
        {claim.length > 0 && (
        <Act
          css="flex:0 0 auto;display:inline-flex;align-items:center;min-height:56px;padding:0 24px;border-radius:14px;font-size:18px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#1F6B52;transition:background .16s ease;"
          disabled={!b}
          reason="Nothing to claim until the benefits feed is connected."
          onClick={() =>
            c.openSheet({
              kicker: 'BENEFITS',
              title: 'Claim what is yours',
              glyph: '✓',
              color: '#1F6B52',
              body: 'Aithora fills in what it already knows and asks you only for what it cannot work out. Most people finish in under ten minutes.',
              rows: facts([['You do', 'answer a few questions out loud'], ['We do', 'the forms, the codes and the chasing'], ['If it is refused', 'we tell you why, in plain words']]),
              primary: 'Start now',
              onPrimary: () => c.act({ kind: 'benefit', target: 'start' }),
            })
          }
        >
          Start the first one
        </Act>
        )}
      </D>
      {items.map((m) => {
        const color = m.claimable ? '#5B6B7C' : m.amountUsd == null ? '#3C454F' : '#22272E';
        const sc = SHEET_COLOR[m.kind];
        return (
          <D key={m.name} css="box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;min-width:0;">
            <S css="display:flex;align-items:baseline;gap:10px;">
              <S css="flex:1 1 0;min-width:0;font-size:16px;font-weight:700;color:#22272E;">{m.name}</S>
              <S css={'font-size:18px;font-weight:700;color:' + color + ';white-space:nowrap;'}>{m.amountUsd == null ? (m.kind === 'medicare' ? '-' : 'unknown') : usd(m.amountUsd)}</S>
            </S>
            <S css="display:block;font-size:16px;color:#54606C;margin-top:1px;">{m.per}</S>
            <S css="display:block;font-size:16px;color:#3C454F;line-height:1.5;margin-top:8px;text-wrap:pretty;">{m.note}</S>
            <Act
              css={BTN(m.claimable)}
              onClick={() =>
                c.openSheet({
                  kicker: m.sheet.kicker,
                  title: m.sheet.title,
                  glyph: m.sheet.glyph,
                  color: sc,
                  stat: m.sheet.stat ? [m.sheet.stat[0], m.sheet.stat[1], sc] : undefined,
                  body: m.sheet.body,
                  rows: numbered(m.sheet.steps.map((s) => [s.k, s.v] as [string, string]), sc),
                  primary: m.sheet.primary,
                  onPrimary: () => c.act({ kind: 'benefit', target: m.kind }),
                })
              }
            >
              {m.action}
            </Act>
          </D>
        );
      })}
      <D css="font-size:16px;color:#54606C;line-height:1.5;padding:0 4px;text-wrap:pretty;">Amounts are plan-year specific and allowances change each plan year, so anything here needs checking against your own plan documents.</D>
    </D>
  );
}
